import {
  answersSaved,
  useOptionalSurvey,
  useSaveQuestionAnswers
} from '~/training/sequence/SequenceAnswers';
import React, { useEffect, useRef, useState } from 'react';
import { Item, registry, RenderProps } from '../Item';
import { DROPDOWN } from '~/training/sequence/templates/items';
import { Button } from '~/ui/buttons';
import { QuizButton } from '~/ui/buttons/QuizButton';
import { PickList } from '~/ui/pick-list/PickList';
import { Modal } from '~/ui/modal/Modal';
import { Overlay } from '~/ui/overlay/Overlay';
import { Flex } from '~/ui/flex/Flex';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { useQuizDropdownStore } from '~/training/sequence/templates/items/quiz/QuizDropdownStore';
import { t } from 'ttag';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { useTrainingStore } from '~/training/state';
import { shouldSendInteractions, useLmsAdapter } from '~/lms';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';

export type DropdownDefinition = {
  label: string;
  correct: boolean;
  feedback: string;
  feedbackAudios?: Array<{ mediaUrl: string; subtitlesUrl?: string }>;
  incorrectFeedbackAudios?: Array<{ mediaUrl: string; subtitlesUrl?: string }>;
  used?: boolean;
  id?: number;
  questionId?: string;
};

interface Props {
  group: string;
  items?: Array<DropdownDefinition>;
  llamaSurvey?: boolean | null;
}

const selector = (s: MediaPlayerStore) => select(s, 'loadMedia', 'reset');

export class DropDown extends Item<typeof DROPDOWN, Props> {
  protected render({ group, id, llamaSurvey }: RenderProps<Props>) {
    // Once the learner completed a pretest in this attempt, surveys are optional, as the server treats them.
    const optionalSurvey = useOptionalSurvey(llamaSurvey === true);
    const savesAnswers = useTrainingStore(s => answersSaved(s, optionalSurvey));
    const saveQuestionAnswers = useSaveQuestionAnswers();
    const { loadMedia, reset } = useMediaPlayerStore(selector);
    const { items, useItem, unUseItem } = useQuizDropdownStore();
    const { addBlocker, removeBlocker } = useSequenceStore();
    const lms = useLmsAdapter();

    const [selected, setSelected] = useState('');
    const [sStatus, setStatus] = useState('');
    const [modal, setModal] = useState(false);
    const [pickListItems, setPickList] = useState<Array<string>>([]);

    const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);
    const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);
    const selectedOption = ((items as Array<DropdownDefinition>) || []).find(
      el => el.label == selected
    );

    if (!group) {
      group = id;
    }

    //generate list items for PickList, because we need hide items if it was selected in another dropdown list
    useEffect(() => {
      const pickListItems: Array<string> = [];

      items.forEach((el: DropdownDefinition) => {
        if (!el?.used) {
          pickListItems.push(el?.label);
        }
      });

      setPickList(pickListItems);
    }, [items]);

    const [_tmp, currentId, questionId] = id.split('_');

    useEffect(() => {
      let mediaUrl = '';
      let subtitlesUrl = '';

      if (selectedOption && selectedOption?.questionId) {
        if (savesAnswers) {
          saveQuestionAnswers(selectedOption.questionId);
        }
      }

      if (selectedOption && selectedOption.feedbackAudios) {
        mediaUrl = selectedOption.feedbackAudios[0]?.mediaUrl as string;
        subtitlesUrl = selectedOption.feedbackAudios[0]?.subtitlesUrl as string;

        //if user select correct option but not in correct place we need play another audio feedback
        if (
          selectedOption?.correct &&
          sStatus == 'wrong' &&
          selectedOption?.incorrectFeedbackAudios
        ) {
          mediaUrl = selectedOption?.incorrectFeedbackAudios[0]
            ?.mediaUrl as string;
          subtitlesUrl = selectedOption?.incorrectFeedbackAudios[0]
            ?.subtitlesUrl as string;
        }
      }

      if (
        selectedOption &&
        !selectedOption.feedbackAudios &&
        selectedOption?.correct &&
        sStatus == 'wrong' &&
        selectedOption?.incorrectFeedbackAudios
      ) {
        mediaUrl = selectedOption?.incorrectFeedbackAudios[0]
          ?.mediaUrl as string;
        subtitlesUrl = selectedOption?.incorrectFeedbackAudios[0]
          ?.subtitlesUrl as string;
      }

      // An optional survey is answered without right or wrong: no feedback audio.
      if (mediaUrl && !optionalSurvey) {
        reset();
        loadMedia(mediaUrl, subtitlesUrl, 'audio', true);
      }
    }, [selectedOption]);

    useEffect(() => {
      if ((!selectedOption || !selectedOption.correct) && !optionalSurvey) {
        addBlocker(group);
        return () => removeBlocker(group);
      }
    }, [selectedOption, optionalSurvey]);

    //Play audio effect
    useEffect(() => {
      const sStatus =
        selectedOption?.correct && Number(currentId) == selectedOption?.id
          ? 'correct'
          : 'wrong';

      setStatus(sStatus);

      if (
        shouldSendInteractions() &&
        lms.isAvailable &&
        selectedOption &&
        selectedOption.id != null &&
        selectedOption.id >= 0
      ) {
        lms.recordTrueFalseInteraction(
          questionId,
          items[selectedOption?.id].label,
          sStatus == 'correct' ? true : false,
          true
        );
      }

      if (selected && !optionalSurvey) {
        sStatus == 'correct'
          ? playAudioEffectRightAnswer()
          : playAudioEffectWrongAnswer();
      }
    }, [selected]);

    const selectItem = (newValue: string) => {
      unUseItem(selected);
      useItem(newValue);
      setSelected(newValue);
      setModal(false);
    };

    const dialogTitleId = `please-select-title-${id}`;
    const dialogRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    // An optional survey shows the word chosen without right or wrong.
    let choiceVariant: 'correct' | 'wrong' | 'llamaSurvey' =
      selectedOption?.correct && Number(currentId) == selectedOption?.id
        ? 'correct'
        : 'wrong';
    if (optionalSurvey) choiceVariant = 'llamaSurvey';

    useFocusTrap(dialogRef, modal, dialogRef, triggerRef);

    useEffect(() => {
      if (!modal) return;

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setModal(false);
        }
      };

      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [modal]);

    return (
      <>
        {!selected ? (
          <Button
            ref={triggerRef}
            onClick={() => setModal(true)}
            aria-haspopup="dialog"
            aria-expanded={modal}
          >
            {t`Please Select`}
          </Button>
        ) : (
          <QuizButton
            ref={triggerRef}
            variant={choiceVariant}
            onClick={() => setModal(true)}
            aria-haspopup="dialog"
            aria-expanded={modal}
          >
            {selected}
          </QuizButton>
        )}

        <Overlay open={modal} onClick={() => setModal(false)}>
          <Modal
            horizontal="center"
            open={modal}
            vertical="center"
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
          >
            <Flex variant="list" ref={dialogRef} tabIndex={-1}>
              <h2 id={dialogTitleId}>{t`Please Select`}</h2>
              {/* CAT-1298: PickList fieldset; legend hidden — dialog title already visible. */}
              <PickList
                selected={selected}
                items={pickListItems}
                onSelect={selectItem}
                legend={t`Please Select`}
                legendVisuallyHidden
                aria-labelledby={dialogTitleId}
              />
            </Flex>
          </Modal>
        </Overlay>
      </>
    );
  }
}

registry[DROPDOWN] = DropDown;
